import { effectByNumber, type EffectsRecord, type Rules } from "odyssey-shared";

/**
 * How much the effects a character bears lower or raise its maximum health, in whole percents, read on the page
 * (ADR-079's amendment of 2026-10-03 for P2.2-T16), so a bar can show the part a penalty locks away. The player's own
 * effects come from the owner's `effects` frame, every effect with its stacks; another character's from its Buffs list,
 * its visible effects without stacks, each read at one stack, which is exact for every shipped effect that moves
 * maximum health (each holds at most one stack). Neither allocates: each walks the list it is given.
 */

/**
 * The summed `maxHealth` modifier of the effects an owner's frame lists, each counted once per stack.
 *
 * @param rules The page's rules, which name each effect by its number.
 * @param frame The decoded `effects` frame, its first `count` entries valid.
 * @returns Whole percents, 0 for none; an effect the rules lack counts nothing.
 * @example
 * frameMaxHealthPercent(rules, { count: 1, effects: [{ effect: 2, ticksLeft: 2400, stacks: 1 }] });
 * // -2: Resurrection Sickness
 */
export function frameMaxHealthPercent(rules: Rules, frame: Readonly<EffectsRecord>): number {
  let percent = 0;
  for (let index = 0; index < frame.count; index += 1) {
    const entry = frame.effects[index];
    const effect = entry === undefined ? undefined : effectByNumber(rules, entry.effect);
    if (entry !== undefined && effect !== undefined) {
      percent += effect.modifiers.maxHealth * Math.max(1, entry.stacks);
    }
  }
  return percent;
}

/**
 * The summed `maxHealth` modifier of the effects a Buffs list names, each at one stack, since the list carries none.
 *
 * @param rules The page's rules, which name each effect by its number.
 * @param buffs The Buffs component's effect numbers.
 * @returns Whole percents, 0 for none; an effect the rules lack counts nothing.
 * @example
 * buffsMaxHealthPercent(rules, [2, 4]);
 * // -17: Resurrection Sickness and Battle Fatigue
 */
export function buffsMaxHealthPercent(rules: Rules, buffs: readonly number[]): number {
  let percent = 0;
  for (let index = 0; index < buffs.length; index += 1) {
    const effect = effectByNumber(rules, buffs[index] as number);
    if (effect !== undefined) {
      percent += effect.modifiers.maxHealth;
    }
  }
  return percent;
}
